import { useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useOpenFeedback, useReplyFeedback } from "@/api/admin/feedback";
import { useConfirmedChange } from "@/components/admin/confirmChange";
import { panelTarget } from "./panelTarget";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { PLATFORM_KEY, displayName, identLabel } from "./userIdentity";

const PAGE: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const ROW: CSSProperties = {
  border: "1px solid var(--d-line)",
  borderRadius: 16,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  background: "var(--d-ink-2)",
};

const META: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  color: "var(--d-fg-muted)",
};

const TEXTAREA: CSSProperties = {
  width: "100%",
  minHeight: 80,
  borderRadius: 12,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-body)",
  fontSize: 14,
  padding: 10,
};

const LINK: CSSProperties = {
  color: "var(--d-violet)",
  textDecoration: "none",
};

const BTN: CSSProperties = {
  alignSelf: "flex-end",
  height: 36,
  padding: "0 16px",
  borderRadius: 999,
  border: "1px solid var(--d-violet)",
  background: "var(--d-violet)",
  color: "#fff",
  fontFamily: "var(--font-body)",
  fontSize: 13,
  cursor: "pointer",
};

export function FeedbackPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const list = useOpenFeedback();
  const reply = useReplyFeedback();
  const { confirmChange, modal } = useConfirmedChange();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [replyStatus, setReplyStatus] = useState<Record<string, string>>({});

  if (list.isLoading) return <div style={PAGE}>{t("feedback.title")}…</div>;
  if (list.isError) return <AdminErrorState onRetry={() => void list.refetch()} />;
  const items = list.data?.items ?? [];

  return (
    <div style={PAGE}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22 }}>
        {t("feedback.title")}
      </h2>
      {items.length === 0 ? (
        <p style={META}>{t("feedback.empty")}</p>
      ) : null}
      {items.map((row) => (
        <div
          key={row.id}
          style={ROW}
          data-testid={`admin-feedback-row-${row.id}`}
        >
          <div style={META}>
            #{row.id} · {row.created_at} · {t(PLATFORM_KEY[row.user.platform])} ·{" "}
            <Link style={LINK} to={`/admin/users/${row.user.id}`}>
              {displayName(row.user) ?? t("users.no_name")}
            </Link>{" "}
            · {identLabel(row.user)} · {row.locale} · {row.category}
          </div>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 15,
              color: "var(--d-fg)",
            }}
          >
            {row.body}
          </p>
          <textarea
            style={TEXTAREA}
            placeholder={t("feedback.reply_placeholder")}
            value={drafts[row.id] ?? ""}
            onChange={(e) =>
              setDrafts((d) => ({ ...d, [row.id]: e.target.value }))
            }
            data-testid={`admin-feedback-reply-${row.id}`}
          />
          <button
            type="button"
            style={BTN}
            disabled={
              reply.isPending || (drafts[row.id]?.trim().length ?? 0) === 0
            }
            onClick={() => {
              const text = (drafts[row.id] ?? "").trim();
              if (text.length === 0) return;
              confirmChange(
                panelTarget("support_reply"),
                "Ticket open",
                `Replied: ${text}`,
                () => reply.mutate(
                { id: row.id, text },
                {
                  onSuccess: () => {
                    setDrafts((d) => {
                      const next = { ...d };
                      delete next[row.id];
                      return next;
                    });
                    setReplyStatus((s) => ({
                      ...s,
                      [row.id]: t("feedback.reply_sent"),
                    }));
                  },
                  onError: () =>
                    setReplyStatus((s) => ({
                      ...s,
                      [row.id]: t("feedback.reply_failed"),
                    })),
                },
                ),
              );
            }}
            data-testid={`admin-feedback-send-${row.id}`}
          >
            {t("feedback.reply_send")}
          </button>
          {replyStatus[row.id] && (
            <div role="status" style={META} data-testid={`admin-feedback-status-${row.id}`}>
              {replyStatus[row.id]}
            </div>
          )}
        </div>
      ))}
      {modal}
    </div>
  );
}

export default FeedbackPage;
